/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : AircraftDetails.tsx
 *
 * Project Title : RDR7000
 *
 * Description   : Aircraft details component
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : August 12, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : August 12, 2020
 * Description   : Created component to show aircraft details in configure step
 *
 *
 ******************************************************************************/

import React, {useState, useEffect} from 'react';
import findIndex from 'lodash/findIndex';
import { useSelector } from 'react-redux';
import { Grid, List, InputLabel, Accordion, Button, Tooltip } from '@scuf/common';
import { FieldArray, useFormikContext, FormikErrors } from 'formik';
import { IConfigureFormInitialValues, IAircraftDetails, ConfigStatusType } from 'models/configuration';
import { selectConfigureScreenData } from 'stores/ConfigureWorkFlowStore';
import { AircraftDetailsInput } from './AircraftDetailsInput';
import { LABELS } from './constants';
import { SoftwareFeaturesItemRadioGroup, SoftwareFeaturesItemSelect } from 'components/SoftwareFeatures';


export const AircraftDetails = () => {
  const { make, model, stc, installMode, productNumber, productDescription, remainingQuantity, totalQuantity, optRemainingQuantity, optTotalQuantity } = useSelector(selectConfigureScreenData);
  const { values, errors, setFieldValue } = useFormikContext<IConfigureFormInitialValues>();

  const purchasedSoftwares = [...values.softwareFeatures].filter(
    soft => soft.editable === true || (soft.editable === false && soft.value === 'disabled')
  );

  
  const [expandPurchase, setExpandPurchase] = useState<boolean[]>([])
  // const [toggleIndex, setToggleIndex] = useState(0)

  

  const onChangeHandler = (item: string, value: string) => {
    const index = findIndex(values.softwareFeatures, { fieldId: item });
    setFieldValue(`softwareFeatures.${index}.value`, value);
  };

  const onChangeHandlerRadio = (item: string, value: string) => {
    const fieldId = item.split("!@##@!")[1];
    const index = findIndex(values.softwareFeatures, { fieldId: fieldId });
    setFieldValue(`softwareFeatures.${index}.value`, value);
  };

  useEffect(()=>{
    let expandPurchaseArr: boolean[] = []
    values.aircraftDetails.forEach((detail, index)=>{
        expandPurchaseArr.push(false)
    }) 
    setExpandPurchase(expandPurchaseArr)
  }, [values.aircraftDetails])

  

  function togglePurchaseFeature (index: number) {
    setExpandPurchase(values=> values.map((value, i)=> i === index ? !value: value))
  }

  return (
    <>
      <Grid className='aircraftDetails'>
        <Grid.Row>
          <Grid.Column width={4}>
            <List bulleted={true}>
              <List.Content>
                <strong>{LABELS.make}</strong> {make}
              </List.Content>
              <List.Content>
                <strong>{LABELS.model}</strong> {model}
              </List.Content>
            </List>
          </Grid.Column>
          <Grid.Column width={8}>
            <List bulleted={true}>
              <List.Content>
                <strong>{LABELS.stc}</strong> {stc}
              </List.Content>
              <List.Content>
                <strong>{LABELS.installMode}</strong> {installMode}
              </List.Content>
            </List>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Grid className='configTitle'>{LABELS.configurationDetails}</Grid>
      <Grid className='configDetails'>
        <Grid.Row style={{paddingBottom:'0px !important'}}>
          <Grid.Column style={{paddingBottom:'0px !important'}} width={4}>
            <List bulleted={true}>
              <List.Content>
                <strong>{LABELS.productNumber}</strong> {productNumber}
              </List.Content>
              <List.Content>
                <strong>{LABELS.quantityAvailable}</strong> {remainingQuantity} of {totalQuantity}
              </List.Content>
            </List>
          </Grid.Column>
          <Grid.Column style={{paddingBottom:'0px !important'}} width={8}>
            <List bulleted={true}>
              <List.Content>
                <strong>{LABELS.productDescription}</strong> {productDescription}
              </List.Content>
            </List>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column className='descColPadding' width={3}>
            <List bulleted={true}>
              <List.Content>
                <strong>{LABELS.softwareFeatures}</strong>
              </List.Content>
            </List>
          </Grid.Column>
          <Grid.Column className='descColPadding' width={9}></Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={12} className='softFeatureDesc'>
            <List>
              <List.Content className='softFeatureList'>
                {values && values?.softwareFeatures.filter((software) => software.value === 'enabled').map((software) => (
                  <List.Content key={software.name}>
                    {software.name}
                  </List.Content>
                ))
                }
              </List.Content>
            </List>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Grid className="aircraft-details-grid">
        {values && values.aircraftDetails && (
          <FieldArray
            name="aircraftDetails"
            render={() => {
              return values.aircraftDetails.map((aircraftDetail, index) => (
                <React.Fragment key={`aircraft-detail-${index}`}>
                  <Grid.Row className="aircraft-details-row">
                    <Grid.Column className='gridHrBox' width={12}>
                      <hr style={{marginBottom:'30px'}} className='breakLine'/>
                    </Grid.Column>
                    <Grid.Column className='gridBoxPadding' width={4}>
                      <InputLabel  label={LABELS.configModuleSerial} indicator="required" />
                      <AircraftDetailsInput
                        name={`aircraftDetails[${index}].configModuleSerialNo`}
                        error={
                          errors && errors.aircraftDetails && typeof errors.aircraftDetails === 'string' && index === 0
                            ? errors.aircraftDetails
                            : errors &&
                            errors.aircraftDetails &&
                            errors.aircraftDetails[index] &&
                            (errors.aircraftDetails as FormikErrors<IAircraftDetails>[])[index].configModuleSerialNo
                        }
                        disabled={aircraftDetail.status === ConfigStatusType.Completed}
                        value={aircraftDetail.configModuleSerialNo}
                        onChange={value => setFieldValue(`aircraftDetails[${index}].configModuleSerialNo`, value)}
                      />
                      {/* <InputLabel label={LABELS.purchasedFeatures} /> */}
                    </Grid.Column>
                    <Grid.Column className='gridBoxPadding' width={3}>
                      <div style={{ minWidth: '100%' }}><InputLabel label={LABELS.tail} indicator="required" /></div>
                      <AircraftDetailsInput
                        name={`aircraftDetails[${index}].tailNo`}
                        error={
                          errors &&
                          errors.aircraftDetails &&
                          errors.aircraftDetails[index] &&
                          (errors.aircraftDetails as FormikErrors<IAircraftDetails>[])[index].tailNo
                        }
                        value={aircraftDetail.tailNo}
                        onChange={value => setFieldValue(`aircraftDetails[${index}].tailNo`, value)}
                      />
                      
                    </Grid.Column>
                    <Grid.Column className='gridBoxPadding' width={3}>
                      <div style={{ minWidth: '100%' }}><InputLabel label={LABELS.acSerial} indicator="required" /></div>
                      <AircraftDetailsInput
                        name={`aircraftDetails[${index}].acSerialNo`}
                        error={
                          errors &&
                          errors.aircraftDetails &&
                          errors.aircraftDetails[index] &&
                          (errors.aircraftDetails as FormikErrors<IAircraftDetails>[])[index].acSerialNo
                        }
                        value={aircraftDetail.acSerialNo}
                        onChange={value => setFieldValue(`aircraftDetails[${index}].acSerialNo`, value)}
                      />
                    </Grid.Column>
                    <Grid.Column className='gridBoxPadding' width={2}>
                      <div style={{ minWidth: '100%' }}><InputLabel label="" /></div>
                      <Tooltip
                        content="Purchased Features"
                        element={
                          <Button
                            className='purchasedFeatureDropDownBtn'
                            type="primary"
                            content=""
                            icon={expandPurchase[index]? "caret-up": "caret-down"}
                            size="medium"
                            onClick={()=>togglePurchaseFeature(index)}
                          />
                        }
                        position="top center"
                        size="small"
                        event="focus"
                        hoverable={true}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {expandPurchase[index] ?
                    <Grid.Row className="aircraft-details-row">
                      <Grid.Column width={12}>
                        <Accordion>
                          <Accordion.Content title={LABELS.purchasedFeatures}
                            active={true}>
                            <Grid className="accordionContentPadding">
                              <Grid.Row>
                                {
                                  purchasedSoftwares && purchasedSoftwares.length !== 0 ?
                                    <>
                                      <Grid.Column width={4}>
                                        <List className="software-features-list">
                                          {purchasedSoftwares.map(softwareFeature => {
                                            const keyValue = aircraftDetail.acSerialNo + softwareFeature.id
                                            const keyName = aircraftDetail.acSerialNo + '!@##@!' + softwareFeature.fieldId
                                            return (
                                              <List.Content key={keyValue}>
                                                {softwareFeature.inputType === 'radio' ? (
                                                  <SoftwareFeaturesItemRadioGroup
                                                    name={keyName}
                                                    label={softwareFeature.name}
                                                    value={softwareFeature.value}
                                                    dataset={softwareFeature.dataset}
                                                    disabled={!softwareFeature.editable}
                                                    onChange={onChangeHandlerRadio}
                                                  />
                                                ) : (
                                                  <SoftwareFeaturesItemSelect
                                                    name={softwareFeature.fieldId}
                                                    label={softwareFeature.name}
                                                    value={softwareFeature.value}
                                                    disabled={!softwareFeature.editable}
                                                    dataset={softwareFeature.dataset}
                                                    onChange={onChangeHandler}
                                                  />
                                                )}
                                                {
                                                  softwareFeature.editable === false && softwareFeature.value === 'disabled' && (
                                                    <div className="incompatible-msg">{LABELS.SOFT_COMPATIBALITY_MSG}</div>
                                                  )
                                                }
                                              </List.Content>
                                            );
                                          })}
                                        </List>
                                      </Grid.Column>
                                      {/* <Grid.Column width={4}><InputLabel label={LABELS.QUANTITY_AVAILABLE} /> {optRemainingQuantity} of {optTotalQuantity}</Grid.Column> */}
                                    </> :
                                    <>
                                      <Grid.Column width={4}>N/A</Grid.Column>
                                    </>}
                              </Grid.Row>
                            </Grid>
                          </Accordion.Content>
                        </Accordion>
                      </Grid.Column>
                    </Grid.Row>
                    : <></>
                  }
                </React.Fragment>
                
              ));
            }}
          />
        )}
      </Grid>
    </>
  );
};
